// Provably Fair — ЕДИНЫЙ источник правды для бэка и фронта.
// Работает и в Node, и в браузере (изоморфная @noble/hashes, без node:crypto).
//
// Модель (по CLAUDE.md §3):
//   commitment = SHA256(serverSeed)                         — публикуется ДО игры
//   hash       = HMAC_SHA256(serverSeed, `${clientSeed}:${nonce}`)
//   из hash детерминированно выводится исход каждой игры.
import { sha256 } from '@noble/hashes/sha256';
import { hmac } from '@noble/hashes/hmac';
import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils';
// ── Константы Dice (тюнятся, RTP проверяется эмпирически) ──────────────────
export const DICE_HOUSE_EDGE = 0.01; // 1%
export const DICE_TARGET_MIN = 0.01;
export const DICE_TARGET_MAX = 98.99;
// ── Криптопримитивы ────────────────────────────────────────────────────────
/** SHA256(input) → hex. Используется для commitment (хеша server seed). */
export function sha256Hex(input) {
    return bytesToHex(sha256(utf8ToBytes(input)));
}
/** HMAC-SHA256(key, message) → hex (64 символа). */
export function hmacSha256Hex(key, message) {
    return bytesToHex(hmac(sha256, utf8ToBytes(key), utf8ToBytes(message)));
}
// ── Dice ───────────────────────────────────────────────────────────────────
/** Бросок 0.00 .. 100.00 (равномерно, 10001 исход). */
export function deriveDiceRoll(serverSeed, clientSeed, nonce) {
    const hash = hmacSha256Hex(serverSeed, `${clientSeed}:${nonce}`);
    return (parseInt(hash.slice(0, 8), 16) % 10001) / 100;
}
/**
 * Множитель выплаты для "roll under target".
 * Честный = 100/target; с house edge: (1 - edge) * 100 / target.
 * Округляем ВНИЗ до 2 знаков — в пользу дома (RTP ≤ 1 - edge).
 */
export function diceMultiplier(target, houseEdge = DICE_HOUSE_EDGE) {
    const m = ((1 - houseEdge) * 100) / target;
    return Math.floor(m * 100) / 100;
}
/** Шанс выигрыша (%) для "roll under target". */
export function diceWinChance(target) {
    return target;
}
/** Победа, если бросок строго меньше target. */
export function isDiceWin(roll, target) {
    return roll < target;
}
/**
 * Полная проверка одной ставки Dice — тот же код выполняет и сервер, и клиент
 * на странице верификации. Возвращает бросок и исход по входным сидам.
 */
export function verifyDiceRoll(input) {
    const { serverSeed, clientSeed, nonce, target, houseEdge = DICE_HOUSE_EDGE } = input;
    const hash = hmacSha256Hex(serverSeed, `${clientSeed}:${nonce}`);
    const roll = (parseInt(hash.slice(0, 8), 16) % 10001) / 100;
    return {
        hash,
        roll,
        win: isDiceWin(roll, target),
        multiplier: diceMultiplier(target, houseEdge),
        chance: diceWinChance(target),
    };
}
// ── Roulette (европейская, один зеро → house edge 2.70% естественным образом) ──
export const ROULETTE_POCKETS = 37; // 0..36
export const ROULETTE_RED = new Set([
    1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36,
]);
export function rouletteColor(n) {
    if (n === 0)
        return 'green';
    return ROULETTE_RED.has(n) ? 'red' : 'black';
}
/** Pocket 0..36 из hash. */
export function deriveRoulettePocket(serverSeed, clientSeed, nonce) {
    const hash = hmacSha256Hex(serverSeed, `${clientSeed}:${nonce}`);
    return parseInt(hash.slice(0, 8), 16) % ROULETTE_POCKETS;
}
/** Полный множитель возврата (включая ставку): straight 36, дюжины/колонки 3, остальное 2. */
export function roulettePayout(type) {
    switch (type) {
        case 'straight':
            return 36;
        case 'dozen1':
        case 'dozen2':
        case 'dozen3':
        case 'column1':
        case 'column2':
        case 'column3':
            return 3;
        default:
            return 2;
    }
}
export function rouletteBetWins(bet, pocket) {
    switch (bet.type) {
        case 'straight':
            return bet.number === pocket;
        case 'red':
            return rouletteColor(pocket) === 'red';
        case 'black':
            return rouletteColor(pocket) === 'black';
        case 'even':
            return pocket !== 0 && pocket % 2 === 0;
        case 'odd':
            return pocket % 2 === 1;
        case 'low':
            return pocket >= 1 && pocket <= 18;
        case 'high':
            return pocket >= 19 && pocket <= 36;
        case 'dozen1':
            return pocket >= 1 && pocket <= 12;
        case 'dozen2':
            return pocket >= 13 && pocket <= 24;
        case 'dozen3':
            return pocket >= 25 && pocket <= 36;
        case 'column1':
            return pocket !== 0 && pocket % 3 === 1;
        case 'column2':
            return pocket !== 0 && pocket % 3 === 2;
        case 'column3':
            return pocket !== 0 && pocket % 3 === 0;
        default:
            return false;
    }
}
export function verifyRoulettePocket(input) {
    const { serverSeed, clientSeed, nonce } = input;
    const hash = hmacSha256Hex(serverSeed, `${clientSeed}:${nonce}`);
    const pocket = parseInt(hash.slice(0, 8), 16) % ROULETTE_POCKETS;
    return { hash, pocket, color: rouletteColor(pocket) };
}
//# sourceMappingURL=index.js.map